import { useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useToast } from "../ui/use-toast";
import { cn } from "../../lib/utils";

export type ImageFieldValue = {
  mode: "none" | "existing" | "upload" | "external" | "base64";
  file?: File | null;
  externalUrl?: string;
  base64?: string;
  previewUrl?: string | null;
};

interface ImageUploaderProps {
  value: ImageFieldValue;
  onChange: (value: ImageFieldValue) => void;
  maxSizeMb?: number;
  existingPreviewUrl?: string | null;
  existingExternalUrl?: string | null;
}

const modes = [
  { id: "upload", label: "Fichier" as const },
  { id: "external", label: "Lien externe" as const },
  { id: "base64", label: "Base64" as const },
];

export const ImageUploader = ({
  value,
  onChange,
  maxSizeMb = 5,
  existingPreviewUrl,
  existingExternalUrl,
}: ImageUploaderProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const releaseObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast({
        title: "Image trop volumineuse",
        description: `La taille maximale autorisée est de ${maxSizeMb} Mo.`,
        variant: "destructive",
      });
      return;
    }
    releaseObjectUrl();
    const previewUrl = URL.createObjectURL(file);
    objectUrlRef.current = previewUrl;
    const reader = new FileReader();
    reader.onloadend = () => {
      const result =
        typeof reader.result === "string" ? reader.result : undefined;
      onChange({
        mode: "upload",
        file,
        previewUrl,
        externalUrl: undefined,
        base64: result,
      });
    };
    reader.onerror = () => {
      toast({
        title: "Erreur lors de la lecture du fichier",
        description: "Impossible de charger cette image.",
        variant: "destructive",
      });
      releaseObjectUrl();
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (objectUrlRef.current && value.previewUrl !== objectUrlRef.current) {
      releaseObjectUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.previewUrl]);

  useEffect(() => {
    return () => {
      releaseObjectUrl();
    };
  }, []);

  const handleExternalChange = (url: string) => {
    onChange({
      mode: "external",
      externalUrl: url,
      previewUrl: url || null,
      file: undefined,
      base64: undefined,
    });
  };

  const handleBase64Change = (data: string) => {
    onChange({
      mode: data.trim() ? "base64" : "none",
      base64: data.trim() || undefined,
      previewUrl: data.trim() || null,
      file: undefined,
      externalUrl: undefined,
    });
  };

  const handleRemove = () => {
    releaseObjectUrl();
    onChange({
      mode: "none",
      previewUrl: null,
      file: undefined,
      externalUrl: undefined,
      base64: undefined,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleKeepExisting = () => {
    onChange({
      mode: "existing",
      previewUrl: existingPreviewUrl ?? null,
      externalUrl: existingExternalUrl ?? undefined,
      file: undefined,
      base64: undefined,
    });
  };

  const showExistingButton =
    Boolean(existingPreviewUrl) && value.mode !== "existing";

  return (
    <div className="space-y-4 rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-white">Visuel illustratif</p>
        <span className="text-xs text-white/60">
          Formats acceptés : JPG, PNG, WEBP (max {maxSizeMb} Mo)
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {modes.map((mode) => (
          <Button
            key={mode.id}
            type="button"
            variant={value.mode === mode.id ? "default" : "ghost"}
            className={cn(
              "rounded-full border border-white/10 bg-white/5 text-xs text-white",
              value.mode === mode.id && "bg-cyan-500 text-slate-900"
            )}
            onClick={() => {
              if (mode.id === "upload") {
                fileInputRef.current?.click();
              } else if (mode.id === "external") {
                handleExternalChange(value.externalUrl ?? "");
              } else {
                handleBase64Change(value.base64 ?? "");
              }
            }}>
            {mode.label}
          </Button>
        ))}
        {showExistingButton && (
          <Button
            type="button"
            variant="ghost"
            className="rounded-full border border-white/10 bg-white/5 text-xs text-white"
            onClick={handleKeepExisting}>
            Conserver l'image actuelle
          </Button>
        )}
        {value.mode !== "none" && (
          <Button
            type="button"
            variant="ghost"
            className="rounded-full border border-white/10 text-xs text-white"
            onClick={handleRemove}>
            Retirer l'image
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {value.mode === "external" && (
        <Input
          type="url"
          value={value.externalUrl ?? ""}
          onChange={(event) => handleExternalChange(event.target.value)}
          placeholder="https://exemple.com/image.png"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
      )}

      {value.mode === "base64" && (
        <Textarea
          value={value.base64 ?? ""}
          onChange={(event) => handleBase64Change(event.target.value)}
          placeholder="Collez ici le code base64 (data:image/png;base64,...)"
          className="bg-white/5 text-white placeholder:text-white/40"
          rows={4}
        />
      )}

      {value.previewUrl ? (
        <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
          <img
            src={value.previewUrl}
            alt="Prévisualisation"
            className="max-h-64 w-full rounded-xl object-cover"
          />
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-white/10 bg-black/30 text-sm text-white/60">
          Aucune image sélectionnée pour le moment.
        </div>
      )}
    </div>
  );
};
